import React from "react";
import {
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import { JsonEditor } from "json-edit-react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import BugReportIcon from "@mui/icons-material/BugReport";

type AdminJsonProps = {
  data: object;
  children?: React.ReactNode;
  raw?: boolean;
  componentName?: string;
  isDebug: boolean;
  otherProps?: Record<string, unknown>;
};

/**
 * AdminJson component renders a collapsible accordion that displays JSON data for debugging purposes.
 *
 * @param {object} props - The properties object.
 * @param {object} props.data - The JSON data to be displayed.
 * @param {React.ReactNode} [props.children=null] - Optional children elements to be rendered inside the accordion.
 * @param {boolean} props.raw - Flag to determine if raw JSON should be displayed in a <pre> tag.
 * @param {string} props.componentName - The name of the component for which the data is being displayed.
 * @param {boolean} props.isDebug - Flag to determine if the debug accordion should be rendered.
 * @param {object} [props.otherProps] - Additional properties to be passed to the Accordion component.
 *
 * @returns {JSX.Element|null} The rendered AdminJson component or null if isDebug is false.
 */
const AdminJson = ({
  data,
  children = null,
  raw,
  componentName,
  isDebug,
  ...otherProps
}: AdminJsonProps) => {
  // Convert data to JSON and back to ensure all Date objects and non-serializable data are properly converted
  const serializedData = JSON.parse(JSON.stringify(data));

  return (
    isDebug && (
      <Accordion {...otherProps} sx={{ mt: 2 }}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel1a-content"
          id="panel1a-header"
        >
          <BugReportIcon sx={{ m: 1 }} />
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ py: 1 }}>{children}</Box>
          <h4>
            {componentName ? `Data for '${componentName}' component` : null}
          </h4>
          <Box
            sx={{
              backgroundColor: "#2d3748",
              color: "#ffffff",
              fontFamily: "monospace",
              padding: "16px",
              borderRadius: "8px",
              "& .jer-editor": {
                backgroundColor: "transparent !important",
              },
            }}
          >
            <JsonEditor
              data={serializedData}
              setData={() => {}} // Read-only mode
              collapse={2}
              restrictEdit={true}
              restrictDelete={true}
              restrictAdd={true}
              restrictTypeSelection={true}
            />
          </Box>
          {raw ? <pre>{JSON.stringify(serializedData, null, 2)}</pre> : null}
        </AccordionDetails>
      </Accordion>
    )
  );
};

export default AdminJson;
